import { SVG_DEFAULT_MARGIN_MM, type Drawing2D, type Vec3 } from './lib'
import { viewBasis, type View2DDef } from './lib'

// ---------------------------------------------------------------------------
// Mapeamento entre as coordenadas do DESENHO (metros no plano da vista) e
// as coordenadas do SVG gerado pelo `drawingToSvg`.
//
// Tem de espelhar EXACTAMENTE a transformação do writer, senão o overlay
// interactivo (linha elástica, marcas de corte) fica desalinhado do
// desenho por baixo:
//
//   k  = 1000 / scaleDenom           mm de papel por metro de modelo
//   sx = (x - bbox.minX) * k + margem
//   sy = (bbox.maxY - y) * k + margem      (o Y do SVG cresce para baixo)
//
// Puro: só números. Testável em Node.
// ---------------------------------------------------------------------------

export interface SvgMetrics {
  /** mm de papel por metro de modelo. */
  k: number
  marginMm: number
  /** Largura/altura do SVG em unidades de viewBox (= mm de papel). */
  width: number
  height: number
  toSvg: (x: number, y: number) => [number, number]
  fromSvg: (sx: number, sy: number) => [number, number]
}

export function svgMetrics(
  d: Drawing2D,
  marginMm = SVG_DEFAULT_MARGIN_MM,
): SvgMetrics {
  const k = 1000 / (d.scaleDenom || 100)
  const width = Math.max((d.bbox.maxX - d.bbox.minX) * k, 0) + marginMm * 2
  const height = Math.max((d.bbox.maxY - d.bbox.minY) * k, 0) + marginMm * 2
  return {
    k,
    marginMm,
    width,
    height,
    toSvg: (x, y) => [
      (x - d.bbox.minX) * k + marginMm,
      (d.bbox.maxY - y) * k + marginMm,
    ],
    fromSvg: (sx, sy) => [
      (sx - marginMm) / k + d.bbox.minX,
      d.bbox.maxY - (sy - marginMm) / k,
    ],
  }
}

/** Ponto do desenho (metros no plano) → ponto do MUNDO.
 *  `p = origin + x·right + y·up`. Exacto: a base da vista é ortonormal
 *  e vem já normalizada do `viewBasis`. */
export function drawingPointToWorld(
  view: View2DDef,
  x: number,
  y: number,
): Vec3 {
  const b = viewBasis(view)
  return [
    b.origin[0] + b.right[0] * x + b.up[0] * y,
    b.origin[1] + b.right[1] * x + b.up[1] * y,
    b.origin[2] + b.right[2] * x + b.up[2] * y,
  ]
}

/** Ponto do MUNDO → coordenadas do desenho (x = right, y = up). */
export function worldPointToDrawing(
  view: View2DDef,
  p: Vec3,
): [number, number] {
  const b = viewBasis(view)
  const dx = p[0] - b.origin[0]
  const dy = p[1] - b.origin[1]
  const dz = p[2] - b.origin[2]
  return [
    dx * b.right[0] + dy * b.right[1] + dz * b.right[2],
    dx * b.up[0] + dy * b.up[1] + dz * b.up[2],
  ]
}

/** Ponto do DESENHO → pixéis do contentor da pré-visualização.
 *
 *  O papel é renderizado com `width: <N>mm` e depois transladado e
 *  escalado por CSS (pan/zoom), portanto a conversão é exacta:
 *  `px = translate + mm * pxPorMm * escala`. Serve para pôr elementos de
 *  HTML (a caixa de texto das anotações) por cima de um ponto do
 *  desenho SEM os meter dentro da camada transformada, onde o `scale`
 *  os esticaria.
 *
 *  `pxPerMm` é injectável para os testes não dependerem do CSS. */
export function drawingPointToContainerPx(
  metrics: { toSvg: (x: number, y: number) => [number, number] },
  x: number,
  y: number,
  transform: { x: number; y: number; scale: number },
  pxPerMm: number,
): [number, number] {
  const [sx, sy] = metrics.toSvg(x, y)
  return [
    transform.x + sx * pxPerMm * transform.scale,
    transform.y + sy * pxPerMm * transform.scale,
  ]
}

/** Coordenadas do rato → coordenadas do SVG (viewBox).
 *
 *  Usa-se a caixa do próprio elemento SVG em vez de
 *  `getScreenCTM().inverse()`: o `viewBox` é sempre `0 0 W H` e o
 *  elemento é uniformemente escalado pelo CSS (pan/zoom da
 *  pré-visualização), portanto a razão é linear e a caixa já traz o
 *  factor de zoom aplicado. Evita a dependência de o browser incluir,
 *  ou não, as transformações CSS dos ascendentes no CTM. */
export function clientToSvg(
  rect: { left: number; top: number; width: number; height: number },
  metrics: { width: number; height: number },
  clientX: number,
  clientY: number,
): [number, number] {
  const sx =
    rect.width > 0
      ? ((clientX - rect.left) / rect.width) * metrics.width
      : 0
  const sy =
    rect.height > 0
      ? ((clientY - rect.top) / rect.height) * metrics.height
      : 0
  return [sx, sy]
}
